import { Moon, PanelLeft, Sun } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { useIsMobile } from '@/components/ui/hooks/use-mobile';
import { cn } from 'helpers/cn';
import { UserAvatar } from '@/components/UserAvatar';
import { userFullName } from 'helpers/user-identity';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/components/ui/ThemeProvider';
import { useWebsites } from '@/state/useWebsites';
import { useAttention } from '@/state/useAttention';
import { NavLink } from '@/components/NavLink';
import { NotificationBell } from './Notifications';
import { UsageMeter } from './UsageMeter';
import { AiCreditMeter } from './AiCreditMeter';
import { ScopePickers } from './ScopePickers';
import markUrl from '@/public/logo/mark-color.svg';

/**
 * The header sits on the desk rather than inside a panel, so it carries no
 * background of its own.
 *
 * On a phone it carries far less. Nine controls need about 540px of width, and a
 * 375px viewport does not have it, so the account and website pickers move into the
 * navigation drawer (`ConsoleSidebar` renders them) and the theme toggle moves into
 * the user menu. What is left is the two things you need from any screen - what is
 * broken, and who you are - plus the way to the navigation.
 */
type ConsoleHeaderProps = {
  onToggleSidebar: () => void;
  /** Full rather than the rail on a desktop; the drawer open on a phone. */
  isSidebarExpanded: boolean;
};

export const ConsoleHeader = ({ onToggleSidebar, isSidebarExpanded }: ConsoleHeaderProps) => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  if (!user) return null;

  // On a desktop the nav is never hidden, only folded to its icons, so the words say so.
  const toggleLabel = isMobile
    ? isSidebarExpanded
      ? 'Hide navigation'
      : 'Show navigation'
    : isSidebarExpanded
      ? 'Collapse navigation'
      : 'Expand navigation';
  const toggleTitle = isMobile ? 'Toggle navigation (⌘B)' : `${toggleLabel} (⌘B)`;

  return (
    <header className="flex h-11 shrink-0 items-center gap-2 px-1 md:gap-3">
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label={toggleLabel}
        aria-expanded={isSidebarExpanded}
        title={toggleTitle}
        className="border-line bg-field text-dim hover:text-foreground focus-visible:ring-ring flex size-9 shrink-0 items-center justify-center rounded-[8px] border transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none md:size-8"
      >
        <PanelLeft className="size-4" strokeWidth={1.5} />
      </button>

      {/* The container variant of the mark. Its clear space is the container
          itself: 8px radius, 6px padding, 1px hairline — do not rebuild it. */}
      <div className="flex items-center gap-2">
        <span className="border-line bg-field flex size-8 items-center justify-center rounded-[8px] border p-1.5">
          <img src={markUrl} alt="" className="h-full w-auto" />
        </span>
        {/* The wordmark is the first thing to go when width is tight: the mark
            already identifies the product. */}
        <span className="hidden text-title font-medium tracking-[-0.01em] sm:inline">pertento</span>
      </div>

      {!isMobile && (
        <>
          <span className="bg-line h-5 w-px" />
          <ScopePickers />
        </>
      )}

      <div className="ml-auto flex items-center gap-2">
        <AlertPill isMobile={isMobile} />
        <UsageMeter isMobile={isMobile} />
        <AiCreditMeter isMobile={isMobile} />
        <NotificationBell />
        {!isMobile && <ThemeToggleButton />}
        <UserMenu showThemeToggle={isMobile} />
      </div>
    </header>
  );
};

const ThemeToggleButton = () => {
  const { theme, setTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label="Toggle theme"
      className="border-line bg-field text-dim hover:text-foreground focus-visible:ring-ring flex size-[30px] items-center justify-center rounded-pill border transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none"
    >
      {isDark ? <Sun className="size-4" strokeWidth={1.5} /> : <Moon className="size-4" strokeWidth={1.5} />}
    </button>
  );
};

/**
 * Summarises what is broken from the same feed the dashboard's attention card
 * reads, so the two can never disagree. Hidden when there is nothing to say.
 *
 * On a phone it drops to counts alone - "2 · 3" against the danger tokens - because
 * the words cost about 110px and the number is the part you act on. The full text
 * stays in the accessible name either way.
 */
type AlertPillProps = { isMobile: boolean };

const AlertPill = ({ isMobile }: AlertPillProps) => {
  const { website } = useWebsites();
  const { items } = useAttention(website?.id);
  if (!items?.length) return null;

  const broken = items.filter((item) => item.severity === 'broken').length;
  const decide = items.filter((item) => item.severity === 'decide').length;
  const parts = [broken && `${broken} broken`, decide && `${decide} to decide`].filter(Boolean) as string[];
  if (!parts.length) return null;

  const compact = [broken || null, decide || null].filter((count) => count !== null).join(' · ');

  return (
    <NavLink
      to="/"
      aria-label={parts.join(', ')}
      title={parts.join(' · ')}
      className="border-danger-line bg-danger-bg text-danger focus-visible:ring-ring shrink-0 rounded-pill border px-2.5 py-1 font-mono text-micro focus-visible:ring-2 focus-visible:outline-none"
    >
      {isMobile ? compact : parts.join(' · ')}
    </NavLink>
  );
};

type UserMenuProps = { showThemeToggle: boolean };

const UserMenu = ({ showThemeToggle }: UserMenuProps) => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  if (!user) return null;

  const isDark = theme === 'dark';

  return (
    <DropdownMenu>
      {/* The ring while open ties the menu back to the badge that opened it; the press
          scale is the only feedback a tap gets on a phone. */}
      <DropdownMenuTrigger
        aria-label="Account menu"
        className={cn(
          'focus-visible:ring-ring ring-offset-background shrink-0 rounded-pill transition-transform focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          'data-[state=open]:ring-accent data-[state=open]:ring-2 data-[state=open]:ring-offset-2',
          'active:scale-[0.94] motion-reduce:transition-none motion-reduce:active:scale-100',
        )}
      >
        <UserAvatar user={user} className="md:size-7" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="flex items-center gap-2.5 px-2 py-1.5">
          <UserAvatar user={user} size="lg" />
          <div className="min-w-0">
            <p className="truncate text-body font-medium">{userFullName(user)}</p>
            <p className="supporting truncate">{user.email}</p>
          </div>
        </div>

        {/* On a phone the theme toggle has no room in the header, so it lands here
            rather than being unavailable. */}
        {showThemeToggle && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setTheme(isDark ? 'light' : 'dark')} className="cursor-pointer text-body">
              {isDark ? (
                <Sun className="mr-2 size-4" strokeWidth={1.5} />
              ) : (
                <Moon className="mr-2 size-4" strokeWidth={1.5} />
              )}
              {isDark ? 'Light mode' : 'Dark mode'}
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <NavLink to="/auth/signout" className={cn('cursor-pointer text-body')}>
            Sign out
          </NavLink>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
